import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ChevronDown, ChevronUp, Copy, Settings2, Trash2, X } from 'lucide-react';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { IndustryTemplate, TemplateField, TemplateFieldKind } from '@/services/industry-configs.service';
import { ENTITY_ORDER, KIND_META, KIND_ORDER, hasOptions, slugifyKey } from './industry-meta';

// Field editing shared by Industry Configs and Blueprints — one field card, one look.

let uidSeq = 0;
export const newUid = () => `f${Date.now().toString(36)}${(uidSeq++).toString(36)}`;

export const keyOf = (f: Draft) => f.key || slugifyKey(f.name);

export type Draft = TemplateField & { uid: string; keyTouched: boolean };

export const QUICK_KINDS: TemplateFieldKind[] = ['text', 'dropdown', 'number', 'date', 'boolean', 'phone'];

export function toDrafts(t: IndustryTemplate | null): Draft[] {
  const fields = [...(t?.fields ?? [])].sort((a, b) =>
    ENTITY_ORDER.indexOf(a.entityType) - ENTITY_ORDER.indexOf(b.entityType) || (a.order ?? 0) - (b.order ?? 0));
  return fields.map((f) => ({ ...f, options: f.options ?? [], uid: newUid(), keyTouched: true }));
}

export function toPayload(f: Draft, order: number): TemplateField {
  const trim = (v?: string) => (v && v.trim() ? v.trim() : undefined);
  return {
    entityType: f.entityType,
    name: f.name.trim(),
    key: keyOf(f),
    kind: f.kind,
    options: hasOptions(f.kind) ? f.options ?? [] : [],
    required: !!f.required,
    order,
    helpText: trim(f.helpText),
    placeholder: trim(f.placeholder),
    defaultValue: trim(f.defaultValue),
  };
}

export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={clsx(
        'relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
        checked ? 'bg-primary' : 'bg-border-strong',
      )}
    >
      <span className={clsx('inline-block h-4 w-4 rounded-full bg-white shadow transition-transform', checked ? 'translate-x-[18px]' : 'translate-x-0.5')} />
    </button>
  );
}

const inputCls =
  'h-10 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-text-primary placeholder:text-text-muted transition-colors ' +
  'hover:border-border-strong focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary-soft';

export function FieldCard({ field: f, first, last, issues, expanded, autoFocus, onChange, onMove, onToggle, onDuplicate, onRemove }: {
  field: Draft;
  first: boolean;
  last: boolean;
  issues?: string[];
  expanded: boolean;
  autoFocus: boolean;
  onChange: (p: Partial<Draft>) => void;
  onMove: (dir: -1 | 1) => void;
  onToggle: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
}) {
  const KIcon = KIND_META[f.kind].icon;
  const nameRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (autoFocus) nameRef.current?.focus(); }, [autoFocus]);
  const bad = !!issues?.length;
  const iconBtn = 'rounded-md p-1.5 text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary disabled:opacity-30 disabled:hover:bg-transparent';

  return (
    <div className={clsx('rounded-xl border bg-surface transition-colors', bad ? 'border-danger/60' : 'border-border hover:border-border-strong')}>
      <div className="flex items-start gap-3 p-4">
        <div className="flex flex-col pt-0.5">
          <button type="button" className={iconBtn} disabled={first} onClick={() => onMove(-1)} aria-label="Move up"><ChevronUp size={14} /></button>
          <button type="button" className={iconBtn} disabled={last} onClick={() => onMove(1)} aria-label="Move down"><ChevronDown size={14} /></button>
        </div>
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
          <KIcon size={16} />
        </span>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-[200px] flex-1">
              <Input
                ref={nameRef}
                value={f.name}
                maxLength={120}
                onChange={(e) => onChange({ name: e.target.value })}
                placeholder="Field name, e.g. Services offered"
                aria-label="Field name"
                invalid={bad && !f.name.trim()}
                className="font-medium"
              />
            </div>
            <Select
              value={f.kind}
              onChange={(e) => onChange({ kind: e.target.value as TemplateFieldKind })}
              aria-label="Field type"
              className="!w-auto"
            >
              {KIND_ORDER.map((k) => <option key={k} value={k}>{KIND_META[k].label}</option>)}
            </Select>
            <button
              type="button"
              onClick={() => onChange({ required: !f.required })}
              aria-pressed={!!f.required}
              className={clsx(
                'h-10 rounded-lg border px-3 text-xs font-semibold transition-colors',
                f.required ? 'border-primary/40 bg-primary-soft text-primary' : 'border-border text-text-muted hover:border-border-strong hover:text-text-primary',
              )}
            >
              {f.required ? 'Required' : 'Optional'}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-2xs text-text-muted">
            <span className="font-medium uppercase tracking-[0.14em]">Key</span>
            <input
              value={keyOf(f)}
              maxLength={120}
              onChange={(e) => onChange({ key: slugifyKey(e.target.value), keyTouched: true })}
              aria-label="Field key"
              className="w-56 rounded-md border border-border bg-surface-alt px-2 py-1 font-mono text-[11px] text-text-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary-soft"
            />
            {!f.keyTouched && f.name && <span>follows the name</span>}
          </div>
          {hasOptions(f.kind) && <OptionsEditor options={f.options ?? []} onChange={(options) => onChange({ options })} />}
          {expanded && (
            <div className="grid gap-3 border-t border-border pt-3 md:grid-cols-3">
              <label className="block">
                <span className="mb-1.5 block text-[13px] font-medium text-text-secondary">Placeholder</span>
                <input value={f.placeholder ?? ''} maxLength={120} onChange={(e) => onChange({ placeholder: e.target.value })} className={inputCls} placeholder="Shown inside the empty input" />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-[13px] font-medium text-text-secondary">Default value</span>
                <DefaultValueInput field={f} onChange={(defaultValue) => onChange({ defaultValue })} />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-[13px] font-medium text-text-secondary">Help text</span>
                <input value={f.helpText ?? ''} maxLength={300} onChange={(e) => onChange({ helpText: e.target.value })} className={inputCls} placeholder="Shown under the field" />
              </label>
            </div>
          )}
          {bad && (
            <ul className="space-y-0.5 text-xs text-danger">
              {issues!.map((m) => <li key={m}>{m}</li>)}
            </ul>
          )}
        </div>
        <div className="flex items-center gap-0.5 pt-1">
          <button type="button" className={clsx(iconBtn, expanded && 'bg-surface-alt text-text-primary')} onClick={onToggle} aria-expanded={expanded} aria-label="More settings" title="Placeholder, default value, help text">
            <Settings2 size={15} />
          </button>
          <button type="button" className={iconBtn} onClick={onDuplicate} aria-label="Duplicate field" title="Duplicate"><Copy size={15} /></button>
          <button type="button" className={clsx(iconBtn, 'hover:bg-danger/10 hover:text-danger')} onClick={onRemove} aria-label="Remove field" title="Remove"><Trash2 size={15} /></button>
        </div>
      </div>
    </div>
  );
}

export function OptionsEditor({ options, onChange }: { options: string[]; onChange: (o: string[]) => void }) {
  const [text, setText] = useState('');
  const add = (raw: string) => {
    const incoming = raw.split(',').map((s) => s.trim()).filter(Boolean);
    if (!incoming.length) return;
    const have = new Set(options.map((o) => o.toLowerCase()));
    const next = [...options];
    for (const o of incoming) if (!have.has(o.toLowerCase())) { next.push(o); have.add(o.toLowerCase()); }
    onChange(next);
    setText('');
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-border bg-surface-alt p-1.5">
      {options.map((o) => (
        <span key={o} className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs font-medium text-text-primary">
          {o}
          <button type="button" onClick={() => onChange(options.filter((x) => x !== o))} className="text-text-muted hover:text-danger" aria-label={`Remove ${o}`}>
            <X size={12} />
          </button>
        </span>
      ))}
      <input
        value={text}
        onChange={(e) => (e.target.value.includes(',') ? add(e.target.value) : setText(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); add(text); }
          if (e.key === 'Backspace' && !text && options.length) onChange(options.slice(0, -1));
        }}
        onBlur={() => add(text)}
        placeholder={options.length ? 'Add another…' : 'Add options — press Enter or type a comma'}
        aria-label="Add option"
        className="min-w-[160px] flex-1 bg-transparent px-1.5 py-1 text-xs text-text-primary placeholder:text-text-muted focus:outline-none"
      />
    </div>
  );
}

function DefaultValueInput({ field: f, onChange }: { field: Draft; onChange: (v: string) => void }) {
  const value = f.defaultValue ?? '';
  if (f.kind === 'boolean') {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        <option value="">No default</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }
  if (f.kind === 'dropdown') {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        <option value="">No default</option>
        {(f.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  const type = f.kind === 'number' || f.kind === 'currency' ? 'number'
    : f.kind === 'date' ? 'date' : f.kind === 'datetime' ? 'datetime-local' : 'text';
  return <input type={type} value={value} maxLength={300} onChange={(e) => onChange(e.target.value)} className={inputCls} placeholder="None" />;
}

